import { RoomsFilters } from "@services/rooms/types";
import {
  asBoolean,
  asNumber,
  asString,
  objectToSearchParams,
  SLFilterToSearch,
} from "./common";
import { getMappedValue } from "./const";
import { convertLocalTime } from "@common/utils/localTime.utils";
import { convertCountryNames } from "@assets/countries";

export const SERVICE_EXPIRATION_TIME_FILTER_SEARCH_KEY =
  "timeServiceExpiration";

export const getTimesForRoomsServiceExpirationFilter = (
  filter: RoomsServiceExpirationTimeFilter,
  payload?: DateInterval
): [start: number, end: number] => {
  let [start, end] = [
    convertLocalTime(new Date()),
    convertLocalTime(new Date()),
  ];
  switch (filter as RoomsServiceExpirationTimeFilter) {
    case "Custom time period":
      let timePayload = payload as DateInterval;
      start = new Date(timePayload?.startDate);
      end = new Date(timePayload?.endDate);
      break;
    case "Today":
      start.setHours(0, 0, 0, 1);
      end.setHours(23, 59, 59, 999);
      break;
    case "In the next 48 hours":
      end.setDate(start.getDate() + 2);
      break;
    case "In the next week":
      end.setDate(start.getDate() + 7);
      break;
    case "In the next month":
      end.setMonth(start.getMonth() + 1);
      break;
  }

  return [convertLocalTime(start).getTime(), convertLocalTime(end).getTime()];
};

export const roomsStatusMap: {
  [K in RoomsStatusFilter]:
    | { key: keyof RoomsFilters; value: boolean }
    | undefined;
} = {
  All: undefined,
  Online: { key: "status.reachable", value: true },
  Offline: { key: "status.reachable", value: false },
  "Monitoring Stalled": {
    key: "status.stale",
    value: true,
  },
  "Monitoring Paused": {
    key: "status.pauseMonitoringStatus.paused",
    value: true,
  },
  "Monitoring Triggers Paused": {
    key: "status.monitoringTriggersStatus.paused",
    value: true,
  },
  "API Error": { key: "status.apiError", value: true },
  "Login Error": { key: "status.loginError", value: true },
  "In a Call": { key: "status.inCall", value: true },
};

const roomsManagementMap: {
  [K in RoomsManagementFilter]:
    | { key: keyof RoomsFilters; value: boolean }
    | undefined;
} = {
  "Managed rooms only": { key: "managed", value: true },
  "All Rooms": { key: "managed", value: false },
};

export const widgetFilterToSLFilter = (
  filters: (WidgetFilter | FilterDefinition)[] = []
): RoomsFilters => {
  let mapped: RoomsFilters = {};
  const mapValue = (
    key: string,
    filterValue: string | number | boolean | string[] | undefined
  ) => {
    let value: typeof filterValue;
    switch (typeof filterValue) {
      case "boolean":
        value = filterValue;
        break;
      case "string":
      case "number":
        value = Boolean(filterValue) ? filterValue : undefined;
        break;
      case "object":
        Array.isArray(filterValue) && filterValue.every((v) => Boolean(v))
          ? (value = filterValue)
          : (value = undefined);
      default:
        value = filterValue;
        break;
    }
    return getMappedValue(
      mapped,
      key as keyof RoomsFilters,
      value
    ) as RoomsFilters;
  };

  filters.forEach((f) => {
    switch (f.filterType as FilterTypes) {
      case "status":
        const statusValue = f.payload
          ? Array.isArray(f.payload)
            ? f.payload
            : [f.payload]
          : [f.filterValue];
        statusValue.forEach((value) => {
          const roomStatus = roomsStatusMap[value as RoomsStatusFilter];
          if (roomStatus !== undefined)
            mapped = mapValue(roomStatus.key, roomStatus.value);
        });
        break;
      case "management":
        const roomManagement =
          roomsManagementMap[f.filterValue as RoomsManagementFilter];
        if (roomManagement !== undefined) {
          mapped = mapValue(roomManagement.key, roomManagement.value);
        }
        break;
      case "vip":
        const vipValue = f.filterValue
          ? f.filterValue === "true" ||
            (f.filterValue as RoomsVIPFilter) === "VIP Rooms"
          : f.filterValue &&
            (f.filterValue === "false" ||
              (f.filterValue as RoomsVIPFilter) === "Non-VIP Rooms")
          ? false
          : undefined;
        mapped = mapValue(f.filterType as keyof RoomsFilters, vipValue);
        break;
      case "service":
        const serviceValue = f.filterValue
          ? f.filterValue === "true" ||
            (f.filterValue as RoomsServiceFilter) === "Active"
          : f.filterValue &&
            (f.filterValue === "false" ||
              (f.filterValue as RoomsServiceFilter) === "Not Active")
          ? false
          : undefined;
        mapped = mapValue(f.filterType as keyof RoomsFilters, serviceValue);
        break;
      case "public":
        const publicValue = f.filterValue
          ? f.filterValue === "true" ||
            (f.filterValue as RoomsPublicFilter) === "Public Room"
          : f.filterValue &&
            (f.filterValue === "false" ||
              (f.filterValue as RoomsPublicFilter) === "Non-Public Room")
          ? false
          : undefined;
        mapped = mapValue("details.publicRoom", publicValue);
        break;
      case "details.roomType":
        mapped = mapValue(
          "details.roomType",
          Array.isArray(f.payload) ? f.payload : undefined
        );
        break;
      case "accountId":
        mapped = mapValue(
          f.filterType as keyof RoomsFilters,
          Boolean(f.filterValue) ? f.filterValue : undefined
        );
        break;
      case SERVICE_EXPIRATION_TIME_FILTER_SEARCH_KEY:
        const [serviceStart, serviceEnd] =
          getTimesForRoomsServiceExpirationFilter(
            f.filterValue as RoomsServiceExpirationTimeFilter,
            f.payload as DateInterval
          );
        mapped = mapValue("details.serviceExpirationDate.after", serviceStart);
        mapped = mapValue("details.serviceExpirationDate.before", serviceEnd);
        break;
      case "tags":
        mapped = mapValue(
          "tags",
          Array.isArray(f.payload) ? f.payload : undefined
        );
        break;
      case "location.country":
        mapped = mapValue(
          "location.country",
          Array.isArray(f.payload) ? f.payload : undefined
        );
        break;
      default:
        mapped = mapValue(f.filterType as keyof RoomsFilters, f.filterValue);
        break;
    }
  });
  return mapped;
};

export const widgetFilterToSearchParams = (
  filters?: (WidgetFilter | FilterDefinition)[]
) => {
  const asSL: any = widgetFilterToSLFilter(filters);
  const serviceExpirationTimeFilterType = filters?.find(
    ({ filterType }) =>
      (filterType as FilterTypes) === SERVICE_EXPIRATION_TIME_FILTER_SEARCH_KEY
  );
  if (serviceExpirationTimeFilterType !== undefined) {
    asSL[SERVICE_EXPIRATION_TIME_FILTER_SEARCH_KEY] = encodeURI(
      serviceExpirationTimeFilterType.filterValue
    );
  }
  if (
    decodeURI(asSL[SERVICE_EXPIRATION_TIME_FILTER_SEARCH_KEY]) !==
    "Custom time period"
  ) {
    delete asSL["details.serviceExpirationDate.after"];
    delete asSL["details.serviceExpirationDate.before"];
  }
  return objectToSearchParams(asSL);
};

export const SLFilterToWidgetFilter = (
  slFilters: RoomsFilters
): FilterDefinition[] => {
  const filters: FilterDefinition[] = [];

  const statusPayload: string[] = [];
  if (slFilters["status.reachable"] !== undefined)
    statusPayload.push(
      slFilters["status.reachable"] === true ? "Online" : "Offline"
    );
  if (slFilters["status.apiError"] !== undefined)
    statusPayload.push("API Error");
  if (slFilters["status.loginError"] !== undefined)
    statusPayload.push("Login Error");
  if (slFilters["status.inCall"] !== undefined) statusPayload.push("In a Call");
  if (slFilters["status.stale"] !== undefined)
    statusPayload.push("Monitoring Stalled");
  if (slFilters["status.pauseMonitoringStatus.paused"] !== undefined)
    statusPayload.push("Monitoring Paused");
  if (slFilters["status.monitoringTriggersStatus.paused"] !== undefined)
    statusPayload.push("Monitoring Triggers Paused");
  if (statusPayload.length > 0)
    filters.push({
      filterType: "status",
      filterValue: "",
      payload: statusPayload,
    });
  if (slFilters["vip"] !== undefined)
    filters.push({
      filterType: "vip",
      filterValue: slFilters["vip"] ? "true" : "false",
    });
  if (slFilters["inService"] !== undefined)
    filters.push({
      filterType: "inService",
      filterValue: slFilters["inService"] ? "true" : "false",
    });
  if (slFilters["details.publicRoom"] !== undefined)
    filters.push({
      filterType: "details.publicRoom",
      filterValue: slFilters["details.publicRoom"] ? "true" : "false",
    });
  if (slFilters["managed"] !== undefined)
    filters.push({
      filterType: "managed",
      filterValue: slFilters["managed"] ? "true" : "false",
    });
  if (slFilters["details.certificationStatus"] !== undefined)
    filters.push({
      filterType: "details.certificationStatus",
      filterValue: slFilters["details.certificationStatus"] ? "true" : "false",
    });
  if (slFilters["details.certifiedBy"] !== undefined)
    filters.push({
      filterType: "details.certifiedBy",
      filterValue: slFilters["details.certifiedBy"],
    });
  if (
    slFilters["details.serviceExpirationDate.after"] &&
    slFilters["details.serviceExpirationDate.before"]
  ) {
    let customValue: RoomsServiceExpirationTimeFilter = "Custom time period";
    filters.push({
      filterType: SERVICE_EXPIRATION_TIME_FILTER_SEARCH_KEY,
      filterValue: customValue,
      payload: {
        startDate: slFilters["details.serviceExpirationDate.after"],
        endDate: slFilters["details.serviceExpirationDate.before"],
      },
    });
  }
  if (slFilters["exchangeId"] !== undefined)
    filters.push({
      filterType: "exchangeId",
      filterValue: slFilters["exchangeId"],
    });
  if (slFilters["name"] !== undefined)
    filters.push({
      filterType: "name",
      filterValue: slFilters["name"],
    });
  if (slFilters["details.contactName"] !== undefined)
    filters.push({
      filterType: "details.contactName",
      filterValue: slFilters["details.contactName"],
    });
  if (slFilters["details.contactEmail"] !== undefined)
    filters.push({
      filterType: "details.contactEmail",
      filterValue: slFilters["details.contactEmail"],
    });
  if (slFilters["details.roomType"] !== undefined)
    filters.push({
      filterType: "details.roomType",
      filterValue: "",
      payload: slFilters["details.roomType"],
    });
  if (slFilters["details.roomNumber"] !== undefined)
    filters.push({
      filterType: "details.roomNumber",
      filterValue: slFilters["details.roomNumber"],
    });
  if (slFilters["location.name"] !== undefined)
    filters.push({
      filterType: "location.name",
      filterValue: slFilters["location.name"],
    });
  if (slFilters["location.country"] !== undefined)
    filters.push({
      filterType: "location.country",
      filterValue: "",
      payload: slFilters["location.country"],
    });
  if (slFilters["accountId"] !== undefined) {
    filters.push({
      filterType: "accountId",
      filterValue: Array.isArray(slFilters["accountId"])
        ? slFilters["accountId"][0]
        : slFilters["accountId"],
    });
  }
  if (slFilters["location.open"] !== undefined)
    filters.push({
      filterType: "location.open",
      filterValue: slFilters["location.open"] ? "true" : "false",
    });

  if (slFilters.tags !== undefined) {
    filters.push({
      filterType: "tags",
      filterValue: "",
      payload: slFilters.tags,
    });
  }

  return filters;
};

export const searchToSLFilter = (params: URLSearchParams): RoomsFilters => {
  const filters: RoomsFilters = {};
  const temp = {
    "details.roomType": params.getAll("details.roomType"),
    accountId: params.getAll("accountId"),
    "location.country": params.getAll("location.country"),
    tags: params.getAll("tags"),
  };

  asBoolean(filters, params, [
    "status.reachable",
    "status.inCall",
    "status.stale",
    "status.apiError",
    "status.loginError",
    "status.pauseMonitoringStatus.paused",
    "status.monitoringTriggersStatus.paused",
    "vip",
    "inService",
    "details.publicRoom",
    "details.certificationStatus",
    "managed",
    "location.open",
  ]);
  asString(filters, params, [
    "location.id",
    "exchangeId",
    "name",
    "details.contactName",
    "details.contactEmail",
    "details.roomType",
    "details.roomNumber",
    "location.name",
    "details.certifiedBy",
  ]);
  asNumber(filters, params, [
    "details.serviceExpirationDate.after",
    "details.serviceExpirationDate.before",
  ]);

  if (temp["details.roomType"].length > 0)
    filters["details.roomType"] = temp["details.roomType"];
  if (temp.accountId.length > 0) filters.accountId = temp.accountId;
  if (temp["location.country"].length > 0)
    filters["location.country"] = convertCountryNames(temp["location.country"]);
  if (temp.tags.length > 0) filters.tags = temp.tags;

  return filters;
};

const searchToWidgetFilter = (params: URLSearchParams): FilterDefinition[] => {
  let definition = SLFilterToWidgetFilter(searchToSLFilter(params));
  let serviceExpirationTimeFilterType = decodeURI(
    params.get(SERVICE_EXPIRATION_TIME_FILTER_SEARCH_KEY) ?? ""
  );
  if (
    serviceExpirationTimeFilterType !== "" &&
    serviceExpirationTimeFilterType !== "Custom time period"
  ) {
    definition.push({
      filterType: SERVICE_EXPIRATION_TIME_FILTER_SEARCH_KEY,
      filterValue: serviceExpirationTimeFilterType,
    });
  }
  return definition;
};

export default {
  fromDefinition: {
    toSL: (filters: (WidgetFilter | FilterDefinition)[] = []) =>
      widgetFilterToSLFilter(filters),
    toSearchParams: widgetFilterToSearchParams,
  },
  fromSL: {
    toDefinition: SLFilterToWidgetFilter,
    toSearchParams: (r: RoomsFilters) => {
      return SLFilterToSearch(r);
    },
  },
  fromSearchParams: {
    toSL: (params: URLSearchParams) => searchToSLFilter(params),
    toDefinition: searchToWidgetFilter,
  },
};
